import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as TicketSizesActions from '../../../../actions/ticketSizes'
import { useAppDispatch } from '../../../../actions/types'
import { colors, Routes } from '../../../../constants'
import {
    Button,
    Column,
    Confirmation,
    Form,
    Headings,
    Input,
    Padding,
} from '../../../../components'
import { Label } from '../../../../components/Form'
import type { TicketSize } from '../../../../types'
import type { TicketSizeFormData } from '../types'
import useTicketSizeParams from '../useTicketSizeParams'
import * as Validation from '../validation'
import ErrorMessages from './ErrorMessages'
import useTicketSizeFormData from './useTicketSizeFormData'
import { FullWidthLabel, NameSection } from '../../Promos/PromoForm/PromoForm.styles'

const sizeOptions = [
    { name: 'ticket-small', value: 'ticket-small' },
    { name: 'ticket-medium', value: 'ticket-medium' },
    { name: 'ticket-large', value: 'ticket-large' },
]

export default function TicketSizeForm({ ticketSize }: { ticketSize?: TicketSize }) {
    const dispatch = useAppDispatch()
    const history = useHistory()
    const { t } = useTranslation()
    const { estate } = useTicketSizeParams()
    const {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        ticketSizeData,
        saveProgress,
        setTicketSizeData,
        setUnsavedChanges,
        submitError,
    } = useTicketSizeFormData(ticketSize)

    const {
        id: ticket_size_id,
        start_date,
        end_date,
        skus,
        storecodes,
        size,
    } = ticketSizeData

    const buildOnChange = (attr: Exclude<keyof TicketSizeFormData, 'start_date' | 'end_date'>) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)

            let value: string | string[] = event.target.value

            if (attr === 'skus' || attr === 'storecodes') {
                value = (value as string).replace(/(?<=\w)(\s)$/, ',')
                    .split(',')
                    .map(item => item.replace(/[^\w]/g, ''))
            }

            setTicketSizeData(prevData => {
                return {
                    ...prevData,
                    [attr]: value,
                }
            })
        }
    }

    const onChangeDates = (attr: 'end_date' | 'start_date') => {
        return (value: number) => {
            setUnsavedChanges(true)
            setTicketSizeData(prevData => {
                return {
                    ...prevData,
                    [attr]: new Date(value),
                }
            })
        }
    }

    const onDeleteTicketSize = () => {
        if (!ticket_size_id) {
            return
        }

        const fn = async () => {
            await dispatch(TicketSizesActions.deleteTicketSize(ticket_size_id))

            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_TICKET_SIZES, { estate })
            )
        }

        fn()
    }

    const isNew = !ticketSize

    const thereAreErrors =
        (validation: Validation.TicketSizeValidation) => Object.keys(validation).length > 0

    const firstError = thereAreErrors(formErrors)
        ? formErrors[Object.keys(formErrors)[0] as keyof Validation.TicketSizeValidation]
        : undefined

    const SkusLabel = skus.length === 0
        ? t('ticket-size-skus')
        : `${t('ticket-size-skus')} (${skus.length})`

    const StorecodesLabel = storecodes.length === 0
        ? t('ticket-size-storecodes')
        : `${t('ticket-size-storecodes')} (${storecodes.length})`

    return (
        <Padding>
            <Form>
                <NameSection top bottom>
                    <Column width="40%">
                        <Headings.H2>
                            {isNew ? t('ticket-size-create') : t('ticket-size-title')}
                        </Headings.H2>
                    </Column>
                    <Column width="60%" right>
                        <ErrorMessages
                            saveProgress={saveProgress}
                            areUnsavedChanges={areUnsavedChanges}
                            errorMessage={firstError ?? submitError?.message}
                        />
                        <Button
                            theme="forestGreen"
                            onClick={onSubmit}
                            disabled={!areUnsavedChanges}
                        >
                            {t('ticket-size-save')}
                        </Button>

                        <Confirmation
                            cancel={t('cancel')}
                            confirmation={t('ticket-size-remove-confirm-body')}
                            header={t('ticket-size-remove-confirm-header')}
                            onCancel={() => null}
                            onConfirm={onDeleteTicketSize}
                            ok={t('ticket-size-remove-confirm-button')}
                            theme="danger"
                        >
                            <Button
                                theme="danger"
                                disabled={isNew}
                            >
                                {t('ticket-size-delete')}
                            </Button>
                        </Confirmation>
                    </Column>
                </NameSection>
                <Padding color={colors.white}>
                    <Column width="60%">
                        {!isNew && (
                            <Label id="ticket-size-id" label={t('ticket-size-id')} error={null}>
                                <Input
                                    id="ticket-size-id"
                                    name="ticket-size-id"
                                    type="text"
                                    size="sm"
                                    value={String(ticket_size_id)}
                                    disabled
                                />
                            </Label>
                        )}
                        <Label id="ticket-size-size" label={t('ticket-size-size')} error={null}>
                            <Input
                                id="ticket-size-size"
                                name="ticket-size-size"
                                type="select"
                                error={formErrors.size && t(formErrors.size)}
                                onChange={buildOnChange('size')}
                                size="sm"
                                value={size}
                                values={sizeOptions.map(option => ({ ...option, name: t(option.name) }))}
                            />
                        </Label>
                        <Label id="ticket-size-start-date" label={t('ticket-size-start-date')} error={null}>
                            <Input
                                id="ticket-size-start-date"
                                name="ticket-size-start-date"
                                type="date"
                                error={formErrors.start_date && t(formErrors.start_date)}
                                onChange={onChangeDates('start_date')}
                                size="sm"
                                value={start_date instanceof Date ? start_date.getTime() : start_date}
                            />
                        </Label>
                        <Label id="ticket-size-end-date" label={t('ticket-size-end-date')} error={null}>
                            <Input
                                id="ticket-size-end-date"
                                name="ticket-size-end-date"
                                type="date"
                                error={formErrors.end_date && t(formErrors.end_date)}
                                onChange={onChangeDates('end_date')}
                                size="sm"
                                value={end_date instanceof Date ? end_date.getTime() : end_date}
                            />
                        </Label>
                    </Column>
                    <Column width="40%" left>
                        <FullWidthLabel id="ticket-size-skus" label={SkusLabel} error={null}>
                            <Input
                                id="ticket-size-skus"
                                name="ticket-size-skus"
                                type="textarea"
                                error={formErrors.skus && t(formErrors.skus)}
                                onChange={buildOnChange('skus')}
                                size="lg"
                                value={skus.join(', ') || ''}
                                style={{ height: '85px', width: '100%' }}
                                placeholder="732009, 512795..."
                            />
                        </FullWidthLabel>
                        <FullWidthLabel id="ticket-size-storecodes" label={StorecodesLabel} error={null}>
                            <Input
                                id="ticket-size-storecodes"
                                name="ticket-size-storecodes"
                                type="textarea"
                                error={formErrors.storecodes && t(formErrors.storecodes)}
                                onChange={buildOnChange('storecodes')}
                                size="lg"
                                value={storecodes.join(', ') || ''}
                                style={{ height: '85px', width: '100%' }}
                                placeholder={t('ticket-size-storecodes-placeholder')}
                            />
                        </FullWidthLabel>
                    </Column>
                </Padding>
            </Form>
        </Padding>
    )
}
